import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Pencil, Trash2, Phone, Briefcase, Users as UsersIcon, ShieldCheck, Store,
  IndianRupee, Clock, CalendarCheck, Wallet, CheckSquare, ArrowRight, Package, Clapperboard,
} from "lucide-react";
import { getEmployee, deleteEmployee } from "../../api/employees";
import { getEmployeeSalary } from "../../api/payroll";
import { useAuth } from "../../context/AuthContext";
import { Card, Button, Skeleton, ConfirmDialog, Avatar, EmptyState } from "../../components/ui";
import { StatusChip } from "../../components/Chips";
import { readCurrentSalary, formatMoney } from "../../lib/salary";
import ShiftSummary from "../../components/WorkingDays";
import EmployeeFormModal from "./EmployeeFormModal";
import { AlertTriangle } from "lucide-react";

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { partner } = useAuth();
  // Both tiers manage staff (docs §4) — a chain across all of its branches,
  // a branch over its own. Only the branch *label* differs between them.
  const isChain = partner?.kind === "CHAIN";
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [payroll, setPayroll] = useState(null);

  // Pay in force right now. Prefer the payroll record, since that is where
  // salary actually lives; fall back to the employee record in case the list
  // response embeds it.
  const currentSalary = readCurrentSalary(payroll) ?? readCurrentSalary(employee);

  async function load() {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await getEmployee(id);
      setEmployee(res.data);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }

    // §11.2 — pay lives in its own collection, so it is a second, optional
    // read. Staff records stay fully usable if it fails or if this login is
    // not allowed to see payroll.
    try {
      const res = await getEmployeeSalary(id);
      setPayroll(res.data);
    } catch {
      setPayroll(null);
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function confirmDelete() {
    setDeleting(true);
    try {
      await deleteEmployee(id);
      navigate("/employees");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-3xl mx-auto">
          <Skeleton className="h-6 w-32" />
          <Card className="mt-5 p-4 sm:p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <div className="flex items-center gap-4">
              <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32 sm:w-40" />
                <Skeleton className="h-4 w-20 sm:w-28" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (notFound || !employee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
          <EmptyState
            icon={AlertTriangle}
            title="Employee not found"
            description="This record may have been removed or doesn't belong to your organization."
            action={
              <Link to="/employees">
                <Button variant="secondary" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  Back to employees
                </Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-3xl mx-auto">
        <Link to="/employees" className="mb-4 sm:mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors duration-200">
          <ArrowLeft size={15} /> Back to employees
        </Link>

        <Card className="p-4 sm:p-6 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar name={employee.name} size={48} className="bg-gradient-to-br from-emerald-500 to-green-600 text-white text-lg shadow-lg shadow-emerald-500/20" />
              <div>
                <h1 className="text-lg sm:text-headline-md text-gray-800 font-bold">{employee.name}</h1>
                <p className="text-xs sm:text-body-sm text-gray-500">
                  {employee.designation || "No designation set"} · {employee.userId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 flex-1 sm:flex-none text-sm"
              >
                <Pencil size={15} className="hidden sm:inline mr-1" /> Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
                className="text-white hover:bg-red-50 hover:text-red-700 flex-1 sm:flex-none text-sm bg-red-500"
              >
                <Trash2 size={15} className="hidden sm:inline mr-1" /> Delete
              </Button>
            </div>
          </div>

          {/* Drill-downs. Each opens the full page already filtered to this
              person via ?employee=<id>, rather than a cut-down copy of it —
              so the filters, pagination and actions there all still work. */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 border-t border-emerald-100/60 pt-5">
            <Link to={`/attendance?employee=${employee._id}`} className="block">
              <DrillButton icon={CalendarCheck} label="Attendance" hint="Punches & corrections" />
            </Link>
            <Link to={`/payroll?employee=${employee._id}`} className="block">
              <DrillButton icon={Wallet} label="Payroll" hint="Earned, paid & due" />
            </Link>
            <Link to={`/todos?employee=${employee._id}`} className="block">
              <DrillButton icon={CheckSquare} label="Daily tasks" hint="Today's list & history" />
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-emerald-100/60 pt-6">
            <InfoRow icon={Phone} label="Phone" value={`+91 ${employee.phone}`} />
            <InfoRow icon={ShieldCheck} label="Status" value={<StatusChip status={employee.status} />} />
            <InfoRow icon={Briefcase} label="Designation" value={employee.designation || "—"} />
            {/* A chain's roster spans every outlet, so where someone works is
                the one detail it can't infer from context. */}
            {isChain && (
              <InfoRow
                icon={Store}
                label="Works at"
                value={
                  employee.partner
                    ? employee.partner.name || "Branch"
                    : "Head office"
                }
              />
            )}
            <InfoRow
              icon={UsersIcon}
              label="Reports to"
              value={
                employee.reportingTo?.length
                  ? employee.reportingTo.map((m) => m.name).join(", ")
                  : "No manager assigned"
              }
            />
            {/* Pay is revision history — this is the figure in force now.
                Hidden entirely when the record carries no salary. */}
            {currentSalary != null && (
              <InfoRow
                icon={IndianRupee}
                label="Monthly salary"
                value={formatMoney(currentSalary)}
              />
            )}
            {/* §12 — the shift is what turns punches into a classified day and
                makes week-offs payable, so it belongs on the record itself. */}
            <InfoRow icon={Clock} label="Shift" value={<ShiftSummary shift={employee.shift} size="md" />} />
          </div>

          {/* Inventory access permissions — §4.1 / §4.5 */}
          <div className="mt-6 border-t border-emerald-100/60 pt-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Package size={15} className="text-emerald-600" />
                Inventory access
              </h2>
            </div>
            {(() => {
              const perms = Array.isArray(employee.permissions) ? employee.permissions : [];
              const ALL_PERMS = [
                { key: "inventory.view",    label: "View stock",       description: "See catalogue and current stock levels" },
                { key: "inventory.record",  label: "Record entries",   description: "Submit receipts, consumption, wastage" },
                { key: "inventory.history", label: "View history",     description: "Browse past submissions and movement ledger" },
              ];
              if (perms.length === 0) {
                return (
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                    No inventory access — this employee cannot see or record stock in the app.
                  </p>
                );
              }
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {ALL_PERMS.map((p) => {
                    const granted = perms.includes(p.key);
                    return (
                      <div
                        key={p.key}
                        className={`rounded-lg border p-3 ${
                          granted
                            ? "border-emerald-300 bg-emerald-50"
                            : "border-gray-100 bg-gray-50/50 opacity-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`h-2 w-2 rounded-full ${granted ? "bg-emerald-500" : "bg-gray-300"}`} />
                          <span className={`text-xs font-semibold ${granted ? "text-emerald-800" : "text-gray-500"}`}>
                            {p.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 pl-4">{p.description}</p>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Cinema (movie keys) permissions */}
          <div className="mt-6 border-t border-emerald-100/60 pt-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Clapperboard size={15} className="text-blue-600" />
                Cinema access
              </h2>
            </div>
            {(() => {
              const perms = Array.isArray(employee.permissions) ? employee.permissions : [];
              const CINEMA_PERMS = [
                { key: "movieKeys.view",    label: "View keys",      description: "See keys held in their name" },
                { key: "movieKeys.consume", label: "Consume keys",   description: "Key off a show — spend a key" },
                { key: "movieKeys.history", label: "View history",   description: "Browse own key movement history" },
              ];
              const hasCinema = perms.some(p => p.startsWith("movieKeys."));
              if (!hasCinema) {
                return (
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                    No cinema access — this employee cannot manage keys in the app.
                  </p>
                );
              }
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {CINEMA_PERMS.map(p => {
                    const granted = perms.includes(p.key);
                    return (
                      <div
                        key={p.key}
                        className={`rounded-lg border p-3 ${
                          granted
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-100 bg-gray-50/50 opacity-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`h-2 w-2 rounded-full ${granted ? "bg-blue-500" : "bg-gray-300"}`} />
                          <span className={`text-xs font-semibold ${granted ? "text-blue-800" : "text-gray-500"}`}>
                            {p.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 pl-4">{p.description}</p>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {employee.reportees?.length > 0 && (
            <div className="mt-6 border-t border-emerald-100/60 pt-6">
              <h2 className="mb-3 text-sm font-semibold text-gray-700">Direct reportees ({employee.reportees.length})</h2>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {employee.reportees.map((r) => (
                  <span
                    key={r._id}
                    className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-body-sm text-gray-700"
                  >
                    {r.name}
                    {r.designation && <span className="text-gray-500"> · {r.designation}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>

        <EmployeeFormModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          employee={employee}
          currentSalary={currentSalary}
          onSaved={() => {
            setEditOpen(false);
            load();
          }}
        />

        <ConfirmDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={confirmDelete}
          loading={deleting}
          danger
          title="Delete employee?"
          description={`${employee.name} will be removed from employee lists and unlinked from the reporting hierarchy.`}
          confirmLabel="Delete"
        />
      </div>
    </div>
  );
}

function DrillButton({ icon: Icon, label, hint }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-white px-3.5 py-2.5 transition-all duration-200 hover:border-emerald-400 hover:bg-emerald-50 hover:shadow-sm">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
        <Icon size={17} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        <p className="truncate text-xs text-gray-500">{hint}</p>
      </div>
      <ArrowRight size={15} className="ml-auto shrink-0 text-gray-300" />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
        <Icon size={15} />
      </div>
      <div>
        <p className="text-xs sm:text-body-sm text-gray-500">{label}</p>
        <div className="mt-0.5 text-sm sm:text-body-md text-gray-800 font-medium">{value}</div>
      </div>
    </div>
  );
}